import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, List, ListOrdered, Link as LinkIcon, Braces, Star } from "lucide-react";

export const MERGE_FIELDS = [
  ["buyer_name", "Buyer Name"], ["buyer_first_name", "Buyer First Name"], ["buyer_email", "Buyer Email"], ["buyer_phone", "Buyer Phone"],
  ["buyer2_name", "Buyer 2 Name"], ["buyer2_first_name", "Buyer2 First Name"], ["buyer2_phone", "Buyer 2 Phone"],
  ["seller_name", "Seller Name"], ["seller_first_name", "Seller First Name"], ["seller_email", "Seller Email"], ["seller_phone", "Seller Phone"],
  ["seller2_name", "Seller 2 Name"], ["seller2_first_name", "Seller2 First Name"], ["seller2_phone", "Seller 2 Phone"],
  ["buyer_agent_name", "Buyer Agent"], ["buyer_agent_phone", "Buyer Agent Phone"], ["seller_agent_name", "Seller Agent"], ["seller_agent_phone", "Seller Agent Phone"],
  ["property_address", "Property Address"], ["purchase_price", "Purchase Price"], ["earnest_money", "Earnest Money Amount"],
  ["contract_date", "Contract Date"], ["closing_date", "Closing Date"], ["possession_date", "Possession Date"],
  ["earnest_money_deadline", "Earnest Money Deadline"], ["inspection_deadline", "Inspection Deadline"],
  ["lender_name", "Lender"], ["lender_phone", "Lender Phone"], ["title_company_name", "Title Company"], ["title_company_email", "Title Company Email"], ["title_company_phone", "Title Company Phone"],
  ["loan_processor_name", "Loan Processor"], ["loan_processor_email", "Loan Processor Email"], ["loan_processor_phone", "Loan Processor Phone"],
  ["listing_url", "Zillow / Listing URL"],
  ["coordinator_name", "Assigned Coordinator"],
  ["coordinator_email", "Coordinator Email"], ["coordinator_phone", "Coordinator Phone"],
];

export function MergeMenu({ onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const btnRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const toggle = (e) => {
    e.preventDefault();
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const width = 256;
      setPos({ top: r.bottom + 6, left: Math.min(Math.max(8, r.right - width), window.innerWidth - width - 8) });
      setQ("");
    }
    setOpen((o) => !o);
  };
  const ql = q.trim().toLowerCase();
  const items = MERGE_FIELDS.filter(([tok, label]) => !ql || label.toLowerCase().includes(ql) || tok.includes(ql));
  const recents = (() => {
    if (ql) return [];
    let usage = {};
    try { usage = JSON.parse(localStorage.getItem("mergeFieldUsage") || "{}"); } catch { usage = {}; }
    return MERGE_FIELDS
      .filter(([tok]) => usage[tok] > 0)
      .sort((a, b) => (usage[b[0]] || 0) - (usage[a[0]] || 0))
      .slice(0, 4);
  })();
  const pick = (tok) => {
    try {
      const usage = JSON.parse(localStorage.getItem("mergeFieldUsage") || "{}");
      usage[tok] = (usage[tok] || 0) + 1;
      localStorage.setItem("mergeFieldUsage", JSON.stringify(usage));
    } catch { /* ignore */ }
    onPick(`{{${tok}}}`);
    setOpen(false);
  };
  return (
    <>
      <button ref={btnRef} type="button" data-testid="merge-menu-btn" onMouseDown={toggle}
        className="inline-flex items-center gap-1 text-xs text-[#1F3C88] font-semibold hover:text-[#152A61]">
        <Braces className="w-3.5 h-3.5" /> Field
      </button>
      {open && createPortal(
        <>
          <div data-testid="merge-menu-overlay" className="fixed inset-0 z-[9998]" onMouseDown={(e) => { e.preventDefault(); setOpen(false); }} />
          <div style={{ top: pos.top, left: pos.left, width: 256 }} className="fixed z-[9999] max-h-80 overflow-hidden flex flex-col bg-white border border-[#E5E5E2] rounded-lg shadow-2xl">
            <div className="p-2 border-b border-[#F0EFEC] bg-white">
              <input data-testid="merge-field-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} onMouseDown={(e) => e.stopPropagation()}
                placeholder="Search fields…" className="w-full px-2.5 py-1.5 text-sm bg-[#F8F8F6] border border-[#E5E5E2] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
            </div>
            <div className="overflow-auto py-1">
              {recents.length > 0 && (
                <div data-testid="merge-recents">
                  <div className="px-3 py-1.5 text-[10px] tracking-[0.12em] uppercase text-[#a3a099] font-semibold">Frequently used</div>
                  {recents.map(([tok, label]) => (
                    <button key={`r-${tok}`} type="button" data-testid={`merge-recent-${tok}`}
                      onMouseDown={(e) => { e.preventDefault(); pick(tok); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-[#F0EFEC] flex justify-between items-center gap-3">
                      <span className="text-[#0A0A0A] flex items-center gap-1.5"><Star className="w-3 h-3 text-[#F7941D] fill-[#F7941D]" />{label}</span><span className="text-[10px] text-[#a3a099] shrink-0">{`{{${tok}}}`}</span>
                    </button>
                  ))}
                  <div className="my-1 border-t border-[#F0EFEC]" />
                </div>
              )}
              {items.map(([tok, label]) => (
                <button key={tok} type="button" data-testid={`merge-field-${tok}`}
                  onMouseDown={(e) => { e.preventDefault(); pick(tok); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-[#F0EFEC] flex justify-between items-center gap-3">
                  <span className="text-[#0A0A0A]">{label}</span><span className="text-[10px] text-[#a3a099] shrink-0">{`{{${tok}}}`}</span>
                </button>
              ))}
              {items.length === 0 && <div className="px-3 py-3 text-xs text-[#a3a099]">No fields match "{q}"</div>}
            </div>
          </div>
        </>, document.body)}
    </>
  );
}

export function RichTextEditor({ value, onChange, placeholder, testid, mergeFields = false }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false }),
      Link.configure({ openOnClick: false, autolink: false, HTMLAttributes: { rel: "noopener noreferrer" } }),
      Placeholder.configure({ placeholder: placeholder || "" }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML() === "<p></p>" ? "" : editor.getHTML()),
    editorProps: { attributes: { class: "rich-editor px-3 py-2 text-sm min-h-[72px] focus:outline-none text-[#0A0A0A]", "data-testid": testid } },
  });

  if (!editor) return null;

  const active = (name, attrs) => editor.isActive(name, attrs);
  const btn = (on) => `p-1.5 rounded hover:bg-[#EFEEEA] transition-colors ${on ? "bg-[#E7ECF7] text-[#1F3C88]" : "text-[#575755]"}`;
  const addLink = (e) => {
    e.preventDefault();
    const url = window.prompt("Link URL (https://…)");
    if (url) editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="border border-[#E5E5E2] rounded-sm overflow-hidden bg-white">
      <div className="flex items-center gap-0.5 px-2 py-1 border-b border-[#E5E5E2] bg-[#FBFAF8]">
        <button type="button" title="Bold" className={btn(active("bold"))} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }}><Bold className="w-4 h-4" /></button>
        <button type="button" title="Italic" className={btn(active("italic"))} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }}><Italic className="w-4 h-4" /></button>
        <button type="button" title="Bullet list" className={btn(active("bulletList"))} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBulletList().run(); }}><List className="w-4 h-4" /></button>
        <button type="button" title="Numbered list" className={btn(active("orderedList"))} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleOrderedList().run(); }}><ListOrdered className="w-4 h-4" /></button>
        <button type="button" title="Link" className={btn(active("link"))} onMouseDown={addLink}><LinkIcon className="w-4 h-4" /></button>
        {mergeFields && <div className="ml-auto"><MergeMenu onPick={(tok) => editor.chain().focus().insertContent(tok).run()} /></div>}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
